import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import { Alert, Box, Breadcrumbs } from '@mui/material';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { getItem, saveItem, updateItem } from './ItemService';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useFormik } from 'formik';
import * as yup from 'yup';

import ItemChildrenList from './ItemChildrenList';
import ItemQuestionList from './ItemQuestionList';
import ItemDocumentList from './ItemDocumentList';
import ItemAttachmentList from './ItemAttachmentList';

const ItemDetail = (): JSX.Element => {
  const { sectionid, itemid } = useParams();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const validationSchema = yup.object({
    sequence: yup.string().required('Sequence is required.'),
    name: yup.string().required('Name is required.'),
    description: yup.string().required('Description is required.'),
    versionNumber: yup.string().required('Version Number is required.'),
    buildNumber: yup.string().required('Build Number is required.'),
    appCode: yup.string().required('App Code is required.'),
    status: yup.string().required('Status is required.'),
    startDate: yup.date().nullable().required('Start Date is required.'),
    endDate: yup
      .date()
      .nullable()
      .min(yup.ref('startDate'), 'End Date cannot be before Start Date')
      .required('End Date is required.'),
  });

  const formik = useFormik({
    initialValues: {
      sequence: '',
      name: '',
      sectionId: '',
      description: '',
      versionNumber: '',
      buildNumber: '',
      appCode: '',
      addonCode: '',
      status: '',
      startDate: '',
      endDate: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const item = {
        ...values,
        startDate: values.startDate
          ? new Date(values.startDate).toISOString()
          : null,
        endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
      };

      if (itemid) {
        updateItem(parseInt(itemid), item)
          .then(() => {
            setIsSaved(true);
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      } else {
        saveItem(parseInt(sectionid ?? '0'), item)
          .then((response) => {
            const newItemId = response.data.id;
            setIsSaved(true);
            navigate(
              `/system/dashboard/section/${sectionid}/item/${newItemId}`,
            );
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      }
    },
  });

  useEffect(() => {
    if (itemid) {
      getItem(parseInt(itemid))
        .then((response) => {
          formik.setValues({
            sequence: response.data.sequence,
            name: response.data.name,
            sectionId: response.data.sectionId,
            description: response.data.description,
            versionNumber: response.data.versionNumber,
            buildNumber: response.data.buildNumber,
            appCode: response.data.appCode,
            addonCode: response.data.addonCode ?? '',
            status: response.data.status,
            startDate: response.data.startDate
              ? formatDateForInput(response.data.startDate)
              : '',
            endDate: response.data.endDate
              ? formatDateForInput(response.data.endDate)
              : '',
          });
        })
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }, [itemid]);

  // Helper function to format date to 'YYYY-MM-DDTHH:mm'
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);

    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the date is invalid
    }

    // Format date for input (YYYY-MM-DDTHH:mm)
    return date.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href={`/system/dashboard/items/section/${sectionid}`}
            >
              Items
            </Link>
            <Typography color="text.primary">Item Detail</Typography>
          </Breadcrumbs>
        </Box>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Item Detail</Typography>
        </Box>
        <Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sequence"
                    name="sequence"
                    fullWidth
                    value={formik.values.sequence}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.sequence && Boolean(formik.errors.sequence)
                    }
                    helperText={
                      formik.touched.sequence && formik.errors.sequence
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    label="Name"
                    name="name"
                    fullWidth
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    label="Description"
                    name="description"
                    fullWidth
                    multiline
                    rows={4} // Adjust the number of rows as needed
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.description &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Version Number"
                    name="versionNumber"
                    fullWidth
                    value={formik.values.versionNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.versionNumber &&
                      Boolean(formik.errors.versionNumber)
                    }
                    helperText={
                      formik.touched.versionNumber &&
                      formik.errors.versionNumber
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Build Number"
                    name="buildNumber"
                    fullWidth
                    value={formik.values.buildNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.buildNumber &&
                      Boolean(formik.errors.buildNumber)
                    }
                    helperText={
                      formik.touched.buildNumber && formik.errors.buildNumber
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="App Code"
                    name="appCode"
                    fullWidth
                    value={formik.values.appCode}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.appCode && Boolean(formik.errors.appCode)
                    }
                    helperText={formik.touched.appCode && formik.errors.appCode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Add-on Code"
                    name="addonCode"
                    fullWidth
                    value={formik.values.addonCode}
                    onChange={formik.handleChange}
                    placeholder="e.g. MORTGAGE_PREPARATION"
                    helperText="Leave blank for item-defined programme. System codes: FINANCE, DOCUMENT_COACH, MORTGAGE_PREPARATION, TAX_PREPARATION, HEALTH_RECORDS, MEDICATION_TRACKING, ESTATE_PLANNING, CONTRACTS"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Status"
                    name="status"
                    fullWidth
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={formik.touched.status && formik.errors.status}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start Date"
                    type="datetime-local"
                    value={formik.values.startDate || ''}
                    onChange={(e) =>
                      formik.setFieldValue('startDate', e.target.value)
                    }
                    fullWidth
                    error={
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate)
                    }
                    helperText={
                      formik.touched.startDate && formik.errors.startDate
                    }
                    InputLabelProps={{
                      shrink: true, // Ensures the label floats when there is a value
                    }}
                    InputProps={{
                      value: formatDateForInput(formik.values.startDate), // Properly format for display
                    }}
                    // style={{
                    //   visibility: formik.values.startDate
                    //     ? 'visible'
                    //     : 'hidden',
                    // }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End Date"
                    type="datetime-local"
                    value={formik.values.endDate || ''}
                    onChange={(e) =>
                      formik.setFieldValue('endDate', e.target.value)
                    }
                    fullWidth
                    error={
                      formik.touched.endDate && Boolean(formik.errors.endDate)
                    }
                    helperText={formik.touched.endDate && formik.errors.endDate}
                    InputLabelProps={{
                      shrink: true, // Ensures the label floats when there is a value
                    }}
                    InputProps={{
                      value: formatDateForInput(formik.values.endDate), // Properly format for display
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    marginBottom={4}
                  >
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate(`/system/dashboard/section/${sectionid}/items`)
                      }
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </LocalizationProvider>
        </Box>
        <Box>
          {(itemid || isSaved) && (
            <Box marginTop={4}>
              <ItemChildrenList parentId={itemid} sectionId={sectionid} />
              <ItemQuestionList itemId={itemid} />
              <ItemDocumentList itemId={itemid} />
              <ItemAttachmentList itemId={itemid} />
            </Box>
          )}
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default ItemDetail;
